/**
 * Client Dimensions state management
 */
import { createAction, getType } from "typesafe-actions";

import api from "../../services/api";

import {
  ClientDimension,
  ClientDimensionInstance,
} from "../../types/models";
import { ThunkResult } from "../../types/redux";
import { RootAction } from "../actions";
import { DEFAULT_CLIENT_DIMENSION_NAME } from "src/components/organisms/Dimensions/ClientDimensionColumn";

export type ClientDimensionsState = ClientDimension[];

// Initial state
const initialState: ClientDimensionsState = [];

// Actions
export const clientDimensionActions = {
  setClientDimensions: createAction("@clientDimensions/setClientDimensions")<
    ClientDimension[]
  >(),
  addClientDimension: createAction("@clientDimensions/addClientDimension")<
    {Name: string}
  >(),
  clearUnsavedClientDimensions: createAction("@clientDimensions/clearUnsavedClientDimensions")(),
  setClientDimensionInstances: createAction(
    "@clientDimensions/setClientDimensionInstances"
  )<{
    clientDimensionId: number;
    instances: ClientDimensionInstance[];
  }>(),
  addClientDimensionInstance: createAction(
    "@clientDimensions/addClientDimensionInstance"
  )<{
    Name: string
  }>(),
};

// Reducer
export function clientDimensionsReducer(
  state = initialState,
  action: RootAction
): ClientDimensionsState {
  switch (action.type) {
    case getType(clientDimensionActions.setClientDimensions):
      return (action as any).payload;
    case getType(clientDimensionActions.clearUnsavedClientDimensions):
      return [
        ...state.filter(d => d.Name !== DEFAULT_CLIENT_DIMENSION_NAME),
      ];
    case getType(clientDimensionActions.addClientDimension):
      return [
        ...state,
        (action as any).payload,
      ];
    case getType(clientDimensionActions.setClientDimensionInstances):
      const { clientDimensionId, instances } = (action as any).payload;
      return state.map((dim) =>
        dim.id === clientDimensionId
          ? { ...dim, client_dimension_instances: instances }
          : dim
      );
    case getType(clientDimensionActions.addClientDimensionInstance):
      const { clientDimensionId: dimId, instance } = (action as any).payload;
      return state.map((dim) =>
        dim.id === dimId
          ? {
              ...dim,
              client_dimension_instances: [
                instance,
                ...(dim.client_dimension_instances || []),
              ],
            }
          : dim
      );
    default:
      return state;
  }
}

// Action Creators / Thunks
export const getClientDimensionsData =
  (clientId: number): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .getClientDimensions(clientId)
      .then((data) => {
        const clientDimensions = data.filter((i: any) => i.Name !== DEFAULT_CLIENT_DIMENSION_NAME);
        dispatch(clientDimensionActions.setClientDimensions(clientDimensions));
        return clientDimensions;
      })
      .catch((error) => {
        console.error("Error fetching client dimensions:", error);
        throw error;
      });
  };

export const createClientDimensionData =
  (clientId: number, name: string): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .createClientDimension(clientId, name)
      .then((data) => {
        dispatch(clientDimensionActions.clearUnsavedClientDimensions());
        dispatch(clientDimensionActions.addClientDimension(data));
        return data;
      })
      .catch((error) => {
        console.error("Error creating client dimension:", error);
        throw error;
      });
  };

export const getClientDimensionInstancesData =
  (clientId: number, clientDimensionId: number): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .getClientDimensionInstances(clientId, clientDimensionId)
      .then((data) => {
        dispatch(
          clientDimensionActions.setClientDimensionInstances({
            clientDimensionId,
            instances: data,
          })
        );
        return data;
      })
      .catch((error) => {
        console.error("Error fetching client dimension instances:", error);
        throw error;
      });
  };

export const createClientDimensionInstanceData =
  (
    clientId: number,
    clientDimensionId: number,
    name: string
  ): ThunkResult<Promise<any>> =>
  (dispatch) => {
    return api
      .createClientDimensionInstance(clientId, clientDimensionId, name)
      .then((data) => {
        // dispatch(
        //   clientDimensionActions.addClientDimensionInstance({
        //     Name: name,
        //   })
        // );
        return data;
      })
      .catch((error) => {
        console.error("Error creating client dimension instance:", error);
        throw error;
      });
  };
